import {Button, DialogActions, DialogTitle, TextField} from "@mui/material";
import {AddItem} from "../project/shopdrawing/freeformchanges/products/add/AddHardware";
import {
    AdjustCol,
    CentsCol,
    NumberCol,
    RenderStringCol,
    Table,
    useTableRefresher,
} from "../../misc/scroller/Table";
import {
    useMemoryTableFetcher2
} from "../project/shopdrawing/freeformchanges/products/add/useMemoryTableFetcher";
import React, {useContext, useMemo, useState} from "react";
import {useSyncedRef} from "../../misc/SyncedRef";
import {Product} from "../../api/Products";
import {api} from "../../api/API";
import {first, formatCents} from "nate-react-api-helpers";
import {removeRowOption, RowOptionCol} from "../logistics/purchasing/RowOptions";
import {CostHelperProvider} from "../project/shopdrawing/pricing/CostHelperContext";
import {CostHelper} from "../project/shopdrawing/pricing/CostHelper";
import {CostFocuser} from "./CostFocuser";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {formatDescription} from "../logistics/purchasing/toSource/SubmitButton";
import {toDescriptionInputProduct} from "../project/quote/pricing/QuotePDF";
import {DoorBuilder} from "../manufacturing/DoorBuilder";
import {FrameBuilder} from "../manufacturing/FrameBuilder";
import {DropDownContext, useDropdownData} from "../project/settings/DropDowns";

export type PriceLookupItem = {
    id: number;
    product: number;
    productObj: Product;
    qty: number;
    updatedAt: string;

    productType: string;
    itemName: string | null;

    unitPrice: number
    extendedPrice: number
}

export const tableName = "price-lookup";

export function PriceLookup(props: {
    actions?: any;
}) {
    return (
        <CostHelperProvider>
            <PriceLookupInner actions={props.actions} />
        </CostHelperProvider>
    )
}

function PriceLookupInner(props: {
    actions?: any;
}) {
    const [value, setValue] = useState<PriceLookupItem[]>([]);
    const ds = useMemoryTableFetcher2(value, setValue);
    const valueRef = useSyncedRef(value);

    const [markupStr, setMarkupStr] = useState("0");

    const subTotal = valueRef.current.reduce((a, b) => a + b.extendedPrice, 0);
    const markupPercent = useMemo(() => {
        try {
            return parseFloat(markupStr) / 100;
        } catch (e) {
            return 0;
        }
    }, [markupStr]);
    const markupExt = subTotal * markupPercent
    const total = markupExt + subTotal;

    const tblRefresh = useTableRefresher(tableName);
    const [frame, setFrame] = useState(false);
    const [door, setDoor] = useState(false);

    const ddlCtx = useContext(DropDownContext);
    const ddlOptions = useDropdownData(ddlCtx.root);

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflow: "hidden",
        }}>
            <DialogTitle>
                Price Lookup
            </DialogTitle>
            <div style={{flex: 1, display: "flex", flexDirection: "column"}}>
                <div style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    paddingRight: 10,
                }}>
                    <div style={{flex: 1}}>
                        <AddItem manualDimension={true} onAdd={async (v) => {

                            const prices = await api.pricing.priceLookup({
                                product: v.id,
                            });

                            const price = first(prices, p => true);

                            ds.onChange({
                                id: -1, // insert
                                product: v.id,
                                productObj: v,
                                qty: 1,
                                updatedAt: new Date().toISOString(),
                                productType: v.kind,
                                itemName: v.categoryName,
                                unitPrice: price?.unitCostCents || 0,
                                extendedPrice: price?.unitCostCents || 0,
                            });

                            tblRefresh();
                        }} />
                    </div>

                    <Button onClick={() => setFrame(true)} endIcon={<OpenInNewIcon />}>
                        Add Frame
                    </Button>
                    <Button onClick={() => setDoor(true)} endIcon={<OpenInNewIcon />}>
                        Add Door
                    </Button>
                </div>
                {door && <DoorBuilder
                    simpleQuoteMode
                    onCancel={() => setDoor(false)}
                    onDone={async v => {
                        const prices = await api.pricing.getCostInOtherQuotes({
                            product: v.id,
                            project: 0,
                        });

                        const price = first(prices, p => true);


                        let input: PriceLookupItem = {
                            id: 0,
                            productObj: v,
                            product: v.id,
                            qty: 1,
                            updatedAt: new Date().toISOString(),
                            productType: "door",
                            itemName: formatDescription(toDescriptionInputProduct(v, ddlOptions), {ddlOptions}),
                            unitPrice: price?.avgUnitCostCents || 0,
                            extendedPrice: price?.avgUnitCostCents || 0,
                        };

                        ds.onChange(input);
                        tblRefresh();
                        setDoor(false);
                    }} />}
                {frame && <FrameBuilder
                    components={false}
                    onCancel={() => setFrame(false)}
                    simpleQuoteMode
                    onDone={async v => {
                        const prices = await api.pricing.getCostInOtherQuotes({
                            product: v.id,
                            project: 0,
                        });

                        const price = first(prices, p => true);


                        let input: PriceLookupItem = {
                            id: 0,
                            productObj: v,
                            product: v.id,
                            qty: 1,
                            updatedAt: new Date().toISOString(),
                            productType: "frame",
                            itemName: formatDescription(toDescriptionInputProduct(v, ddlOptions), {
                                ddlOptions,
                            }),
                            unitPrice: price?.avgUnitCostCents || 0,
                            extendedPrice: price?.avgUnitCostCents || 0,
                        };

                        ds.onChange(input);
                        tblRefresh();
                        setFrame(false);
                    }} />}

                <Table<PriceLookupItem>
                    name={tableName}
                    cellCustomize={{}}
                    columns={[
                        RowOptionCol({
                            options: (dt:PriceLookupItem) => {
                                return [
                                    removeRowOption(dt, {
                                        refreshTables: [tableName],
                                    })
                                ]
                            },
                        }),
                        RenderStringCol<PriceLookupItem>("Name", r => r.itemName || "", 150),
                        RenderStringCol<PriceLookupItem>("Code", r => r.productObj.productCode, 100),
                        CentsCol("Unit Cost", "unitPrice", 100, {
                            showHintOnFocus: CostFocuser,
                        }),
                        NumberCol("Qty", "qty", 40),
                        AdjustCol(RenderStringCol<PriceLookupItem>("Ext.", d => formatCents(d.qty * d.unitPrice)), {
                            alignRight: true,
                        }),
                    ]}
                    fetch={ds.fetch}
                    fetchDeps={[ds.fetch]}
                    onChange={async v => {
                        v.extendedPrice = v.qty * v.unitPrice
                        ds.onChange(v);
                        tblRefresh();
                    }}
                />

                <CostHelper />
                <div style={{height: 8}} />
                <div style={{display: "flex", justifyContent: "flex-end", alignItems: "flex-start", gap: 10, paddingLeft: 16, paddingRight: 16}}>
                    <div style={{
                        fontSize: "0.8rem",
                    }}>
                        *You can also open this lookup on any project page by pressing shift, shift
                    </div>
                    <div style={{flex: 1}} />
                    <div>
                        <div style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 10,
                        }}>
                            <div style={{
                                fontSize: "0.8rem",
                            }}>Sub-Total</div>
                            <div style={{fontWeight: "bold"}}>${formatCents(subTotal)}</div>
                        </div>
                        <div style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 10,
                            paddingTop: 8,
                            paddingBottom: 8,
                        }}>
                            <div style={{
                                fontSize: "0.8rem",
                            }}>Markup</div>
                            <div>
                                <TextField
                                    label="Markup %" size="small"
                                    value={markupStr} onChange={e => setMarkupStr(e.target.value)}
                                    inputProps={{
                                        style: {
                                            textAlign: "right",
                                        }
                                    }}
                                />
                            </div>
                            <div style={{fontWeight: "bold"}}>
                                ${formatCents(markupExt)}
                            </div>
                        </div>
                        <div style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 10,
                        }}>
                            <div style={{
                                fontSize: "0.8rem",
                            }}>Total</div>
                            <div style={{fontWeight: "bold"}}>${formatCents(total)}</div>
                        </div>
                    </div>
                </div>
            </div>
            <DialogActions>
                <Button color="error" onClick={() => {
                    setValue([]);
                    tblRefresh();
                }}>Reset</Button>
                <div style={{flex:1}} />
                {props.actions}
            </DialogActions>
        </div>
    )
}